import { startTransition, type Dispatch, type ReactNode, type SetStateAction } from 'react'

export interface LiveRender {
  node: ReactNode
  committed: () => void
}

interface Options {
  setLive: Dispatch<SetStateAction<LiveRender | null>>
  retry: () => void
}

export const createLivePreviewCommit = ({ setLive, retry }: Options) => {
  let settle: (() => void) | undefined
  return {
    commit: (node: ReactNode) =>
      new Promise<void>((resolve) => {
        const started = performance.now()
        // Next's action transition can remain suspended after its promise has
        // fulfilled. An urgent render retries that work without replacing the
        // visible tree or sending another request. Run only until DOM commit.
        // https://github.com/vercel/next.js/issues/96233
        const timer = setInterval(retry, 500)
        const complete = () => {
          clearInterval(timer)
          settle = undefined
          resolve()
        }
        settle = complete
        startTransition(() =>
          setLive({
            node,
            committed: () => {
              if (settle !== complete) {
                return
              }
              console.info(
                '[sb-live] live tree committed (%dms after response)',
                Math.round(performance.now() - started)
              )
              complete()
            },
          })
        )
      }),
    dispose: () => settle?.(),
  }
}
